/**
 * Session-related localStorage keys and user-specific storage cleanup.
 *
 * Extracted from SessionProvider so the storage-key tables and the
 * collect-then-remove sweep are React-free and unit-testable. TOKEN_STORAGE_KEY
 * is imported back into SessionProvider (used ~12x); the prefix/key tables and
 * clearUserSpecificStorage are invoked on logout and onboarding-reset.
 */

export const TOKEN_STORAGE_KEY = 'xavior_auth_token'
export const GUEST_ID_STORAGE_KEY = 'xavior_guest_id'

// User-specific localStorage keys that should be cleared on logout or account reset
const USER_SPECIFIC_STORAGE_KEYS = [
  'chat-user-name',
  'chat-assistant-name',
  'xavior_conversation_order',
]

// Prefixes for user-specific keys whose suffix varies per workspace/contact
// and therefore cannot be enumerated up-front. Iterated and removed below.
const USER_SPECIFIC_STORAGE_PREFIXES = [
  // #2715: compose-draft stash. Keyed by workspace+contact; the 24h TTL caps
  // accidental cross-session exposure but logout should hard-clear too.
  'xavior:compose:draft:',
  // #2726: calendar-scope selector (keyed per Events module id) — cleared for
  // symmetry with other user-scoped UI state on account switch.
  'xavior:calendar-scope:',
]

/**
 * Clear user-specific localStorage to prevent stale state
 * Called on logout and when server indicates onboarding not completed
 */
export function clearUserSpecificStorage() {
  if (typeof window === 'undefined') return
  USER_SPECIFIC_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key)
  })
  // Sweep prefix-matched keys (compose drafts and other multi-instance state).
  // Collect first, then remove — mutating localStorage while iterating its
  // indices via `key(i)` would shift remaining indices and skip entries.
  const prefixMatches: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (k && USER_SPECIFIC_STORAGE_PREFIXES.some((p) => k.startsWith(p))) {
      prefixMatches.push(k)
    }
  }
  prefixMatches.forEach((k) => localStorage.removeItem(k))
}
